import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { FacultyLayout } from '../../components/FacultyLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { BookOpen, Users, Calendar, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacultyCourses = () => {
    const { token } = useAuth();
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCourses = async () => {
            try {
                const headers = { Authorization: `Bearer ${token}` };
                const response = await axios.get(`${API_URL}/api/faculty/my-courses`, { headers });
                setCourses(response.data);
            } catch (error) {
                console.error('Error fetching courses:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchCourses();
    }, [token]);

    return (
        <FacultyLayout title="My Courses">
            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <Card key={i} className="animate-pulse">
                            <CardContent className="h-48"></CardContent>
                        </Card>
                    ))}
                </div>
            ) : courses.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {courses.map((course) => (
                        <Card key={course.id} className="card-hover">
                            <CardContent className="p-6">
                                {course.image_url && (
                                    <img
                                        src={course.image_url}
                                        alt={course.name}
                                        className="w-full h-32 object-cover rounded-lg mb-4"
                                    />
                                )}
                                <h3 className="font-heading font-semibold text-lg mb-2">{course.name}</h3>

                                <div className="flex gap-2 mb-3">
                                    <Badge variant="secondary">{course.category}</Badge>
                                    <Badge variant="outline">{course.course_type}</Badge>
                                </div>

                                <div className="space-y-2 text-sm text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4" />
                                        <span>{course.enrolled_count} students enrolled</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-4 h-4" />
                                        <span>{course.batch_timing}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4" />
                                        <span>{course.start_date} - {course.end_date}</span>
                                    </div>
                                </div>

                                <div className="mt-4 pt-4 border-t flex gap-2">
                                    <a
                                        href={`/faculty-portal/syllabus?course=${course.id}`}
                                        className="flex-1 text-center px-3 py-2 text-sm bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors"
                                    >
                                        Upload Syllabus
                                    </a>
                                    <a
                                        href={`/faculty-portal/materials?course=${course.id}`}
                                        className="flex-1 text-center px-3 py-2 text-sm bg-primary/10 text-primary hover:bg-primary/20 rounded-lg transition-colors"
                                    >
                                        Add Materials
                                    </a>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            ) : (
                <Card>
                    <CardContent className="py-16 text-center">
                        <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                        <h3 className="font-heading text-xl font-semibold mb-2">No Courses Assigned</h3>
                        <p className="text-muted-foreground">
                            You haven't been assigned to any courses yet. Please contact the administrator.
                        </p>
                    </CardContent>
                </Card>
            )}
        </FacultyLayout>
    );
};

export default FacultyCourses;
